/*
  Ingredient Atlas home: the featured slider's dots and arrows, and the
  search bar with the quick-look card. Loaded only by the Atlas home
  sections. The cards are plain HTML from the theme, so the page reads fine
  without this script; the bar and the card stay hidden until it runs.
*/

if (!customElements.get('nr-atlas-slider')) {
  class NrAtlasSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      const controls = this.querySelector('[data-controls]');
      if (!this.track || !controls) return;
      controls.hidden = false;
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.current() - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.current() + 1));
      this.track.addEventListener('scroll', () => this.mark(), { passive: true });
    }

    step() {
      const slides = this.track.querySelectorAll('[data-slide]');
      return slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : this.track.clientWidth;
    }

    current() {
      return Math.round(this.track.scrollLeft / this.step());
    }

    go(i) {
      const last = this.dots.length - 1;
      const target = Math.max(0, Math.min(last, i));
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: target * this.step(), behavior: smooth ? 'smooth' : 'auto' });
    }

    mark() {
      const i = this.current();
      this.dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
    }
  }
  customElements.define('nr-atlas-slider', NrAtlasSlider);
}

if (!customElements.get('nr-atlas-shelf')) {
  class NrAtlasShelf extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(document.querySelectorAll('[data-atlas-group]'));
      if (!this.groups.length) return;
      this.section = this.closest('.shopify-section') || this;
      this.input = this.querySelector('[data-search]');
      this.empty = this.querySelector('[data-empty]');
      this.panel = this.querySelector('[data-panel]');
      this.desktop = window.matchMedia('(min-width: 990px)');

      const cards = this.groups.flatMap((g) => Array.from(g.querySelectorAll('[data-card]')));
      if (this.dataset.countText) this.input.placeholder = this.dataset.countText.replace('[count]', cards.length);

      // One jump button per group, named after it, with its count
      const jumps = this.querySelector('[data-jumps]');
      this.groups.forEach((group) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nr-chip';
        button.textContent = group.dataset.title + ' ';
        const count = document.createElement('small');
        count.textContent = group.querySelectorAll('[data-card]').length;
        button.append(count);
        button.addEventListener('click', () => group.querySelector('.nr-ah-group__head').scrollIntoView({ behavior: 'smooth' }));
        jumps.append(button);
      });

      this.input.addEventListener('input', () => this.filter());
      this.groups.forEach((group) =>
        group.addEventListener('click', (event) => {
          const card = event.target.closest('[data-look]');
          if (!card) return;
          // A card with a full entry is also a link: the first tap shows it in
          // the quick-look card, a second tap (or the card's button) opens it.
          // A click with a modifier key still opens it in a new tab.
          if (card.dataset.url) {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            if (card === this.lastCard && this.panel.classList.contains('is-open')) return;
            event.preventDefault();
          }
          this.show(card);
        })
      );
      this.querySelector('[data-close]').addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel.classList.contains('is-open')) this.close();
      });

      this.place = this.place.bind(this);
      this.layout = this.layout.bind(this);
      window.addEventListener('scroll', this.place, { passive: true });
      // The bar slides down under the site header when that shows on scrolling up
      this.section.addEventListener('transitionend', this.place);
      this.desktop.addEventListener('change', this.layout);
      // The groups change height as the window resizes or a search hides cards
      new ResizeObserver(() => this.layout()).observe(document.querySelector('.content-for-layout') || document.body);
      this.hidden = false;
      this.layout();
    }

    filter() {
      const q = this.input.value.trim().toLowerCase();
      let shown = 0;
      this.groups.forEach((group) => {
        let inGroup = 0;
        group.querySelectorAll('[data-card]').forEach((card) => {
          const match = !q || card.dataset.search.includes(q);
          card.hidden = !match;
          if (match) inGroup += 1;
        });
        group.closest('.shopify-section').hidden = inGroup === 0;
        shown += inGroup;
      });
      this.empty.hidden = shown > 0;
      this.layout();
    }

    show(card) {
      const d = card.dataset;
      this.groups.forEach((g) => g.querySelectorAll('[data-look]').forEach((c) => c.setAttribute('aria-pressed', c === card ? 'true' : 'false')));
      const photo = this.panel.querySelector('[data-photo]');
      photo.replaceChildren();
      if (d.img) {
        const img = document.createElement('img');
        img.src = d.img;
        img.alt = d.alt || '';
        img.width = 720;
        img.height = 540;
        photo.append(img);
      } else {
        const short = document.createElement('span');
        short.className = 'nr-ah-card__short';
        short.textContent = d.short || d.name;
        photo.append(short);
      }
      this.panel.querySelector('[data-number]').textContent = d.number;
      this.panel.querySelector('[data-number]').hidden = !d.number;
      this.panel.querySelector('[data-name]').textContent = d.name;
      this.panel.querySelector('[data-kind]').textContent = d.kind;
      this.panel.querySelector('[data-line]').textContent = d.line;
      this.panel.querySelector('[data-ours]').textContent = d.ours || '';
      this.panel.querySelector('[data-ours]').hidden = !d.ours;
      const open = this.panel.querySelector('[data-open]');
      if (d.url) open.href = d.url;
      open.parentElement.hidden = !d.url;
      this.panel.querySelector('[data-soon]').hidden = Boolean(d.url);
      this.panel.querySelector('[data-item]').hidden = false;
      this.panel.classList.add('has-item', 'is-open');
      this.lastCard = card;
      if (!this.desktop.matches) this.panel.querySelector('[data-close]').focus();
    }

    close() {
      this.panel.classList.remove('is-open');
      if (!this.desktop.matches) this.lastCard?.focus();
    }

    // Steps the bar out of the way once the last group has gone by, and keeps
    // the bar's height in a variable for the card's sticky top.
    place() {
      const last = this.visibleGroups().pop() || this.groups[this.groups.length - 1];
      const bar = this.getBoundingClientRect();
      this.section.classList.toggle('is-past', last.getBoundingClientRect().bottom < bar.bottom + 40);
      if (this.desktop.matches) {
        document.documentElement.style.setProperty('--nr-ah-bar-height', `${Math.round(bar.height)}px`);
      }
    }

    visibleGroups() {
      return this.groups.filter((g) => !g.closest('.shopify-section').hidden);
    }

    // On desktop the card sits in a rail laid over the side column of the
    // first group and stretched down to the end of the last one, so it stays
    // in view with position: sticky all the way down the groups, crossing
    // from one group to the next without a jump, and never runs past them.
    // On phones it lives in the bar as a sheet from the bottom of the screen.
    layout() {
      if (!this.desktop.matches) {
        if (this.panel.parentElement !== this) this.append(this.panel);
        this.place();
        return;
      }
      const visible = this.visibleGroups();
      const first = visible[0] || this.groups[0];
      const last = visible[visible.length - 1] || first;
      const side = first.querySelector('.nr-ah-group__side');
      if (!side) return;
      if (!this.rail) {
        this.rail = document.createElement('div');
        this.rail.className = 'nr-ah-group__rail';
      }
      if (this.rail.parentElement !== side) side.append(this.rail);
      if (this.panel.parentElement !== this.rail) this.rail.append(this.panel);
      // Ends 24px short of the last group, the same gap the card keeps under the bar
      const height = last.getBoundingClientRect().bottom - side.getBoundingClientRect().top - 24;
      this.rail.style.height = `${Math.max(0, Math.round(height))}px`;
      this.place();
    }
  }
  customElements.define('nr-atlas-shelf', NrAtlasShelf);
}
